import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Observable, map, of } from 'rxjs';
import {
  BatchWriteMetadataOrRelationListRequest,
  BatchWriteMetadataOrRelationListResponse,
  CoreItemGate,
  GetItemListByTypeListRequest,
} from '../../../../../backend/src/core/core-item/core-item.gate';
import { ResourceName } from '../../../../../backend/src/core/resource-name';
import { environment } from '../../../environments/environment';
import { AsyncServiceBase } from '../../shared/core/async-service-base';
import { LambdaService } from '../../shared/core/lambda.service';
import { UserClerkService } from '../user/user-clerk.service';
import { WorkspaceHolderService } from '../workspace/workspace.holder.service';
import { CoreItem, CoreItemBackend, CoreItemType, VertexRelationship } from './core-item';
import { SortKeyUtils } from './sort-key';
import { SubDataConverter } from './sub-data.converter';

@Injectable({
  providedIn: 'root',
})
export class CoreItemService extends AsyncServiceBase<CoreItemBackend, CoreItem> {
  override convertFromFrontendToBackend(frontend: Partial<CoreItem>): CoreItemBackend {
    const backendBasic = {
      id: frontend.id!,
      sk: frontend.sk!,
      version: frontend.version!,

      title: frontend.title,
      description: frontend.description!,

      parentId: frontend.parentId,
      type: frontend.type,

      subData: SubDataConverter.fromFrontendToBackend(frontend.coreType!, frontend.subData),
      status: frontend.status,
      statusTimeStr: frontend.statusTime?.toUTC().toISO() ?? undefined,

      startTimeStr: frontend.startTime?.toUTC().toISO() ?? undefined,
      endTimeStr: frontend.endTime?.toUTC().toISO() ?? undefined,
    };

    return backendBasic;
  }
  override afterAddItem(frontend: CoreItem): void {}
  override afterDeleteItem(frontend: CoreItem): void {}
  override convertFromBackendToFrontend(backend: CoreItemBackend): CoreItem {
    const coreType = SortKeyUtils.parse(backend.sk).coreItemType!;
    return {
      ...backend,
      subData: SubDataConverter.fromFrontendToBackend(coreType, backend.subData),
      coreType: coreType,
      lastModifiedTime: DateTime.fromISO(backend.lastModifiedTimeStr!),
      createdTime: DateTime.fromISO(backend.createdTimeStr!),
      statusTime: backend.statusTimeStr ? DateTime.fromISO(backend.statusTimeStr) : undefined,

      startTime: backend.startTimeStr ? DateTime.fromISO(backend.startTimeStr) : undefined,
      endTime: backend.endTimeStr ? DateTime.fromISO(backend.endTimeStr) : undefined,
    };
  }
  override cleanTempData(payload?: any): void {}
  override afterUpdateItem(frontend: CoreItem): void {}
  constructor(
    @Inject(HttpClient) http: HttpClient,
    @Inject(LambdaService) lambdaService: LambdaService,
    @Inject(UserClerkService) userClerkService: UserClerkService,
    private router: Router,
    private message: NzMessageService,
    private workspaceHolderService: WorkspaceHolderService
  ) {
    super(http, lambdaService, userClerkService, ResourceName.coreItem);
  }

  updateCoreItem$(frontend: CoreItem, notChangingLoadingStatus = false) {
    return this.updateItemPrivate$(frontend, CoreItemGate.actions.update, true, notChangingLoadingStatus);
  }

  private cacheIdListStrAndData = new Map<string, any>();
  getCoreItemListByIdList$(coreItemList: { id: string; sk: string }[]) {
    const request: { id: string; sk: string }[] = coreItemList.map((c) => ({
      id: c.id,
      sk: c.sk,
    }));

    const key = request.map((c) => c.id).join(',');

    if (this.cacheIdListStrAndData.has(key)) {
      return of(this.cacheIdListStrAndData.get(key));
    }

    return this._lambdaService.run$(this._resourceName, CoreItemGate.actions.getItemListByIdList, request).pipe(
      map((c: any) => {
        if (!c) {
          return [];
        }
        this.cacheIdListStrAndData.set(key, c);
        return c;
      })
    );
  }

  getCoreItemList$(typeList: CoreItemType[], parentId?: string): Observable<CoreItem[]> {
    const request: GetItemListByTypeListRequest = {
      workspaceId: this.workspaceHolderService.currentWorkspace().id,
      typeList,
      parentId,
    };

    return this.getItemOrItemListByAction$(CoreItemGate.actions.getItemListByTypeList, request);
  }

  get cachedItemList() {
    return this._itemOrItemList ? (this._itemOrItemList as CoreItem[]) : [];
  }

  updateCoreItemInCache(backend?: CoreItemBackend) {
    return this.setOneItemToCacheData(backend, true);
  }

  batchWriteMetadataAndRelationList$(options: {
    addRelationList?: VertexRelationship[];
    deleteRelationList?: VertexRelationship[];
    addMetadataList?: Partial<CoreItem>[];
    deleteMetadataList?: { id: string; sk: string }[];
    updateMetadataList?: CoreItem[];
    notChangeLoadingStatus?: boolean;
  }): Observable<CoreItem[]> {
    let observable: Observable<CoreItem[]>;

    if (environment.isConnectToBackend) {
      const totalChangeCount =
        (options.addRelationList?.length ?? 0) +
        (options.deleteRelationList?.length ?? 0) +
        (options.addMetadataList?.length ?? 0) +
        (options.deleteMetadataList?.length ?? 0);

      if (totalChangeCount === 0) {
        return of([]);
      }

      let addMetadataList: any[] = [];
      if (options.addMetadataList) {
        addMetadataList = options.addMetadataList.map((c) => {
          return {
            type: c.coreType,
            metadata: this.convertFromFrontendToBackend(c),
          };
        });
      }

      const updateMetadataBackendList = options.updateMetadataList?.map((c) => this.convertFromFrontendToBackend(c));

      const batchWriteRelationListRequest: BatchWriteMetadataOrRelationListRequest = {
        workspaceId: this.workspaceHolderService.currentWorkspace().id,
        addRelationList: options.addRelationList,
        deleteRelationList: options.deleteRelationList,
        addMetadataList,
        deleteMetadataList: options.deleteMetadataList,
        updateMetadataList: updateMetadataBackendList,
      };
      observable = this._lambdaService
        .run$(
          ResourceName.coreItem,
          CoreItemGate.actions.batchWriteMetadataOrRelationList,
          batchWriteRelationListRequest,
          {
            notChangeLoadingStatus: options.notChangeLoadingStatus,
          }
        )
        .pipe(
          map((response: BatchWriteMetadataOrRelationListResponse) => {
            const addedCoreItemList: CoreItem[] = [];

            response.addedMetadataList?.forEach((c) => {
              const addedCoreItem = this.addBackendToService(c);
              addedCoreItemList.push(addedCoreItem!);
            });

            response.updatedMetadataList?.forEach((c) => {
              this.updateCoreItemInCache(c);
            });

            // response.addedRelationList?.forEach((c) => {
            //   this.setTaskRelationToCache(c);
            // });

            // options.deleteRelationList?.forEach((c) => {
            //   this.deleteRelationFromCache(c);
            // });

            options.deleteMetadataList?.forEach((c) => {
              this.deleteBackendToServicePrivate(c as any);
            });

            return addedCoreItemList;
          })
        );
    } else {
      observable = of([]);
    }
    return observable;
  }
}
